import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, Home as HomeIcon, RotateCcw } from "lucide-react";

// ─────────────────────────────────────────────────────────────
// Approved LAYOR knowledge base (public site content only).
// Every answer mirrors approved website language. No private data.
// ─────────────────────────────────────────────────────────────
const CONTACT_PHONE = "+1-813-344-3188";
const CONTACT_EMAIL = "info@layordigitalgroup.com";

const TOPICS = {
  services: {
    q: "What services does LAYOR provide?",
    a: `LAYOR helps leaders and organizations clarify direction, strengthen decision-making, build leadership capacity, and turn complex priorities into practical action through three connected areas:

ADVISE. Leadership Strategy and Executive Advisory
We help leaders clarify priorities, navigate complexity, strengthen decisions, and prepare their organizations to move forward with confidence. When useful, AI readiness and responsible adoption are integrated to support the leadership strategy.

EQUIP. Leadership Training, Speaking, and Facilitation
We equip leaders and teams through executive learning experiences, strategy labs, institutes, keynotes, retreats, cohorts, and facilitated sessions designed for immediate application.

BUILD. Leadership Tools, Decision Support, and Digital Solutions
We create practical systems, custom assistants, decision-support resources, learning tools, and digital solutions that extend leadership capacity and make complex work easier to manage and execute.

There's also LAYOR Digital Studio. LAYOR Digital Studio is the dedicated design and production division of LAYOR Digital Group. We design and produce executive websites, board and pitch decks, presentations, and digital resources. We also refine, update, enhance, or redesign existing assets to ensure they are strategically aligned, professionally presented, and ready to support the organization's goals.

Where would you like to go next?`,
    followups: [
      { label: "Explore Advisory", action: "route:/services#advise" },
      { label: "Explore Leadership Training", action: "route:/services#equip" },
      { label: "Explore What LAYOR Builds", action: "route:/services#build" },
      { label: "Main Menu", action: "menu" },
    ],
  },
  speaker: {
    q: "How can I book Patricia as a speaker?",
    a: `Patricia is available for executive keynotes, conference sessions, breakout presentations, leadership workshops, facilitated retreats, and multi-session leadership institutes, all customized to your audience, timeline, and desired outcome.

Every speaking engagement begins by understanding the room, the responsibilities the leaders in it are carrying, and what you want them to leave with.

How would you like to proceed?`,
    followups: [
      { label: "Request a Speaking Engagement", action: "route:/quote?service=Keynote%20or%20Conference%20Presentation" },
      { label: "Schedule a Consultation", action: "route:/contact" },
      { label: "Return to Main Menu", action: "menu" },
    ],
  },
  training: {
    q: "What training programs are available?",
    a: `Three signature ways to engage, each designed to extend leadership capacity, not add another initiative to your plate:

• Executive Strategy Lab™: Leadership Velocity (flagship). A 90-minute virtual experience, Driving Enterprise Outcomes with Practical AI™, where leaders move through three tiers: Diagnose Before You Design, Augmented Decision-Making, and Disciplined Execution using APPLY → TIMELINE → SUCCESS CHECK → REFINE → EVALUATE.
• LAYOR Leadership Lab™. A 120-minute hands-on team experience where leaders build practical tools around the work they already lead and leave ready to apply them within 24 hours.
• LAYOR Executive AI Leadership Institute™. A premium, customized, multi-session institutional offering for executive teams, associations, and organizations building durable AI leadership capacity.

Every training is leadership-first, interactive, anchored to authentic leadership challenges, and platform-neutral (participants use their preferred AI platform, such as ChatGPT).

The LAYOR takeaway: you don't just leave informed. You leave equipped, with the practical tools and clarity to lead forward tomorrow.`,
    followups: [
      { label: "Host A Strategy Lab Session", action: "route:/quote?service=Executive%20Strategy%20Lab%20Leadership%20Velocity" },
      { label: "Bring The Leadership Lab To My Team", action: "route:/quote?service=LAYOR%20Leadership%20Lab" },
      { label: "Inquire About The Institute", action: "route:/quote?service=LAYOR%20Executive%20AI%20Leadership%20Institute" },
      { label: "Return to Main Menu", action: "menu" },
    ],
  },
  build: {
    q: "What can LAYOR build for my organization?",
    a: `LAYOR builds strategic solutions designed for immediate application and measurable impact, always customized to real leadership responsibilities and always with human judgment at the center.

Examples of what leaders and teams build with LAYOR:
• AI-assisted decision-making protocols
• Executive communication builders
• Leadership thought-partner assistants
• Meeting preparation and follow-up systems
• Strategic planning tools
• Research and executive-brief workflows
• Custom AI agents for specific roles
• Prompt libraries and leadership toolkits

The LAYOR takeaway: practical solutions your leaders will actually use, designed to extend their expertise, not replace their judgment.`,
    followups: [
      { label: "See The Full Library", action: "route:/what-you-will-build" },
      { label: "Discuss A Build Project", action: "route:/quote?service=Leadership%20Tools%20%26%20Digital%20Solutions" },
      { label: "Schedule a Consultation", action: "route:/contact" },
      { label: "Return to Main Menu", action: "menu" },
    ],
  },
  consultation: {
    q: "How do I schedule a consultation?",
    a: `Every LAYOR engagement begins with a consultation. Share the work you're carrying, and we'll listen to your priorities, understand your organization, and tell you honestly whether advisory, training, a built solution, or a combination is right.

You can also reach us directly:
• Email: ${CONTACT_EMAIL}
• Phone: ${CONTACT_PHONE}

When you're ready:`,
    followups: [
      { label: "Book a Consultation", action: "route:/contact" },
      { label: "Return to Main Menu", action: "menu" },
    ],
  },
  quote: {
    q: "How do I request a quote?",
    a: `Pricing is customized based on the scope and needs of each engagement. Please complete the Quote Request form, and a member of the LAYOR Digital Group team will contact you to discuss your priorities, your audience, and the appropriate options.

A few notes on our approach:
• Every quote is tailored to the work you lead
• Prices may vary at any time
• Each quote is valid for 14 days from the date of issue

When you're ready:`,
    followups: [
      { label: "Request a Quote", action: "route:/quote" },
      { label: "Schedule a Consultation Instead", action: "route:/contact" },
      { label: "Return to Main Menu", action: "menu" },
    ],
  },
};

const MENU_KEYS = ["services", "speaker", "training", "build", "consultation", "quote"];

// Free-text keyword routing → topic key (or null for off-topic)
function classifyFreeText(text) {
  const t = text.toLowerCase();
  if (/(price|cost|how much|fee|quote|proposal|pricing|invoice)/.test(t)) return "quote";
  if (/(speak|speaker|keynote|conference|panel|stage|host|book patricia)/.test(t)) return "speaker";
  if (/(training|workshop|institute|power lab|strategy lab|masterclass|program|cohort|course|learn)/.test(t)) return "training";
  if (/(agent|automation|build|workflow|assistant|tool|system|solution|develop|website|deck|studio)/.test(t)) return "build";
  if (/(consult|consultation|meeting|schedule|call|talk to|reach|contact)/.test(t)) return "consultation";
  if (/(service|help|offer|do you|what do|about layor|patricia)/.test(t)) return "services";
  if (/(hi|hello|hey|good (morning|afternoon|evening))/.test(t)) return "hello";
  return null;
}

const OFFTOPIC_MESSAGE =
  "Thanks for asking! Ask LAYOR is here to help with LAYOR services, programs, speaking, training, consultations, quotes, and digital solutions. Choose a topic below and I'll walk you through it.";

const HELLO_MESSAGE =
  "Hello, and welcome to LAYOR Digital Group! I'm here to help you explore how we work with leaders and organizations. Pick a topic below to get started.";

function makeMenuMessage(intro) {
  return {
    role: "bot",
    text: intro,
    followups: MENU_KEYS.map((k) => ({ label: TOPICS[k].q, action: `topic:${k}` })),
  };
}

const WELCOME = () =>
  makeMenuMessage(
    "Hello! I'm Ask LAYOR, your guide to LAYOR Digital Group. I can share how LAYOR helps leaders through advisory, training and speaking, and custom AI and digital solutions. Choose a question below, or type your own, and we'll keep the conversation going as long as you like."
  );

export default function ChatWidget() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME()]);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("layor:open-chat", handler);
    return () => window.removeEventListener("layor:open-chat", handler);
  }, []);

  // Focus the input when opened; support Escape to close
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      const onKey = (e) => {
        if (e.key === "Escape") setOpen(false);
      };
      window.addEventListener("keydown", onKey);
      return () => {
        clearTimeout(t);
        window.removeEventListener("keydown", onKey);
      };
    }
  }, [open]);

  const openTopic = useCallback((key) => {
    const topic = TOPICS[key];
    if (!topic) return;
    setMessages((m) => [
      ...m,
      { role: "user", text: topic.q },
      { role: "bot", text: topic.a, followups: topic.followups },
    ]);
  }, []);

  const showMenu = useCallback((intro) => {
    setMessages((m) => [
      ...m,
      makeMenuMessage(intro || "Anything else I can help with? Choose a topic below."),
    ]);
  }, []);

  const startOver = useCallback(() => {
    setMessages([WELCOME()]);
    setInput("");
    setTimeout(() => inputRef.current?.focus(), 40);
  }, []);

  const handleFollowup = useCallback(
    (action) => {
      if (!action) return;
      if (action === "menu") {
        showMenu();
        return;
      }
      if (action.startsWith("topic:")) {
        openTopic(action.slice(6));
        return;
      }
      if (action.startsWith("route:")) {
        const to = action.slice(6);
        setOpen(false);
        navigate(to);
      }
    },
    [openTopic, showMenu, navigate]
  );

  const handleFreeText = useCallback(
    (text) => {
      const clean = text.trim();
      if (!clean) return;
      setMessages((m) => [...m, { role: "user", text: clean }]);
      const cls = classifyFreeText(clean);
      if (!cls) {
        setTimeout(() => {
          setMessages((m) => [
            ...m,
            {
              role: "bot",
              text: OFFTOPIC_MESSAGE,
              followups: MENU_KEYS.map((k) => ({ label: TOPICS[k].q, action: `topic:${k}` })),
            },
          ]);
        }, 200);
        return;
      }
      if (cls === "hello") {
        setTimeout(() => {
          setMessages((m) => [
            ...m,
            {
              role: "bot",
              text: HELLO_MESSAGE,
              followups: MENU_KEYS.map((k) => ({ label: TOPICS[k].q, action: `topic:${k}` })),
            },
          ]);
        }, 200);
        return;
      }
      setTimeout(() => openTopic(cls), 200);
    },
    [openTopic]
  );

  const onSubmit = (e) => {
    e.preventDefault();
    handleFreeText(input);
    setInput("");
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-open"
          aria-label="Open Ask LAYOR chat"
          className="fixed bottom-6 right-6 z-40 rounded-full shadow-lg flex items-center gap-2 px-5 py-3.5 text-sm font-semibold text-[var(--layor-navy)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--layor-navy)] focus-visible:ring-offset-2"
          style={{
            backgroundColor: "#C6A85A",
            boxShadow: "0 10px 30px -6px rgba(198,168,90,0.55)",
          }}
        >
          <Sparkles size={16} aria-hidden="true" /> Ask LAYOR
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-label="Ask LAYOR chat"
          className="fixed bottom-6 right-6 z-40 w-[92vw] max-w-[400px] rounded-2xl shadow-2xl border overflow-hidden flex flex-col"
          style={{
            borderColor: "rgba(198,168,90,0.4)",
            backgroundColor: "#fff",
            maxHeight: "min(78vh, 640px)",
          }}
          data-testid="chat-panel"
        >
          {/* Header */}
          <div
            className="px-4 py-3 flex items-center justify-between gap-2"
            style={{ backgroundColor: "var(--layor-navy)" }}
          >
            <div className="flex items-center gap-2 text-white min-w-0">
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: "#C6A85A" }}
              >
                <Sparkles size={14} className="text-[var(--layor-navy)]" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">Ask LAYOR</div>
                <div
                  className="text-[10px] uppercase tracking-[0.24em] truncate"
                  style={{ color: "#E6C871" }}
                >
                  Your LAYOR Guide
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => showMenu("Here's the main menu. Pick any question below.")}
                data-testid="chat-main-menu"
                aria-label="Show main menu"
                className="text-white/85 hover:text-white text-[11px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--layor-gold)] flex items-center gap-1"
                title="Show main menu"
              >
                <HomeIcon size={12} aria-hidden="true" />
                <span className="hidden sm:inline">Menu</span>
              </button>
              <button
                onClick={startOver}
                data-testid="chat-start-over"
                aria-label="Start over"
                className="text-white/85 hover:text-white text-[11px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--layor-gold)] flex items-center gap-1"
                title="Start the conversation over"
              >
                <RotateCcw size={12} aria-hidden="true" />
                <span className="hidden sm:inline">Restart</span>
              </button>
              <button
                onClick={() => setOpen(false)}
                data-testid="chat-close"
                aria-label="Close chat"
                className="text-white/85 hover:text-white p-1 rounded-md hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--layor-gold)]"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Transcript */}
          <div
            ref={scrollRef}
            role="log"
            aria-live="polite"
            aria-atomic="false"
            className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-[var(--layor-ivory)]"
            data-testid="chat-transcript"
          >
            {messages.map((m, i) => (
              <div key={i} className={m.role === "bot" ? "flex flex-col items-start" : "flex flex-col items-end"}>
                <div
                  className={`text-[13px] leading-relaxed rounded-2xl px-3.5 py-2.5 max-w-[92%] whitespace-pre-line ${
                    m.role === "bot"
                      ? "bg-white border border-[var(--layor-border)] text-[var(--layor-navy)]"
                      : "text-white"
                  }`}
                  style={m.role === "user" ? { backgroundColor: "var(--layor-navy)" } : {}}
                  data-testid={m.role === "bot" ? `chat-bot-msg-${i}` : `chat-user-msg-${i}`}
                >
                  {m.text}
                </div>
                {m.role === "bot" && m.followups && m.followups.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5 max-w-[92%]" data-testid={`chat-followups-${i}`}>
                    {m.followups.map((f, j) => {
                      const isMenuOrTopic = f.action === "menu" || f.action.startsWith("topic:");
                      return (
                        <button
                          key={`${i}-${j}`}
                          onClick={() => handleFollowup(f.action)}
                          className={`text-[11px] leading-tight rounded-full px-3 py-1.5 border font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--layor-navy)] ${
                            isMenuOrTopic
                              ? "border-[rgba(10,21,44,0.15)] text-[var(--layor-navy)] bg-white hover:bg-[var(--layor-ivory)] hover:border-[var(--layor-navy)]"
                              : "border-[var(--layor-gold)] text-[var(--layor-navy)] bg-[#FFF9E9] hover:bg-[var(--layor-gold)] hover:text-white"
                          }`}
                          data-testid={`chat-followup-${i}-${j}`}
                        >
                          {f.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Input */}
          <form
            onSubmit={onSubmit}
            className="border-t border-[var(--layor-border)] p-2 flex items-center gap-2 bg-white"
          >
            <label htmlFor="layor-chat-input" className="sr-only">
              Type your message
            </label>
            <input
              id="layor-chat-input"
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message…"
              className="flex-1 text-sm px-3 py-2 rounded-full border border-[var(--layor-border)] focus:outline-none focus:border-[var(--layor-gold)] focus-visible:ring-2 focus-visible:ring-[var(--layor-gold)]"
              data-testid="chat-input"
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={!input.trim()}
              aria-label="Send message"
              className="btn-primary rounded-full h-9 w-9 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--layor-gold)]"
              data-testid="chat-send"
            >
              <Send size={14} aria-hidden="true" />
            </button>
          </form>

          <div className="px-3 pb-2 pt-1 text-[10px] text-[var(--layor-muted)] bg-white leading-snug">
            Ask LAYOR uses public LAYOR website information only. Pricing is customized to each engagement, and every quote is valid for 14 days from the date of issue.
          </div>
        </div>
      )}
    </>
  );
}
